import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useUser from "@saleor/hooks/useUser";
import { findValueInEnum, maybe } from "@saleor/misc";
import useBrandGroupSearch from "@saleor/searches/useBrandGroupSearch";
import useClusterSearch from "@saleor/searches/useClusterSearch";
import {
  BrandVerticalEnum,
  PermissionEnum,
  SmopStatusEnum,
  StoreTypeEnum
} from "@saleor/types/globalTypes";
import React from "react";
import { useIntl } from "react-intl";

import SellerCreatePage, {
  SellerCreatePageFormData
} from "../../components/SellerCreatePage";
import { useSellerCreate } from "../../mutations";
import { BrandCreateSeller } from "../../types/BrandCreateSeller";
import { sellerDetailsUrl, SellerListPath } from "../../urls";
export const SellerCreate: React.FC = () => {
  const notify = useNotifier();
  const navigate = useNavigator();
  const intl = useIntl();

  const {
    user: { userPermissions }
  } = useUser();

  const hasShopifyPermission = userPermissions.some(
    permission => permission.code === PermissionEnum.MANAGE_SHOPIFY
  );

  const searchCluster = useClusterSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      first: 5
    }
  });

  const handleSuccess = (data: BrandCreateSeller) => {
    if (data.brandCreate.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Seller Created"
        })
      });
      navigate(sellerDetailsUrl(data.brandCreate.brand.id), true);
    } else if (data.brandCreate.errors.some(e => e.code === "REQUIRED")) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Some Fields have null value !"
        })
      });
    } else {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "There's an error!"
        })
      });
    }
  };
  const [sellerCreate, sellerCreateResult] = useSellerCreate({
    onCompleted: handleSuccess
  });

  const handleSubmit = async (formData: SellerCreatePageFormData) => {
    const result = await sellerCreate({
      variables: {
        input: {
          accountManager: formData.accountManager,
          bankAccountName: formData.bankAccountName,
          bankAccountNo: formData.bankAccountNo,
          bankName: formData.bankName,
          cluster: formData.cluster,
          code: formData.code,
          completeAddress: formData.completeAddress,
          customerCode: formData.customerCode,
          financeEmail: formData.financeEmail,
          financeMobileNo: formData.financeMobileNo,
          financeName: formData.financeName,
          isLargeTaxpayer: formData.isLargeTaxpayer,
          isPayoutEligible: formData.isPayoutEligible,
          legalName: formData.legalName,
          name: formData.name,
          signatoryDesignation: formData.signatoryDesignation,
          signatoryEmail: formData.signatoryEmail,
          signatoryMobileNo: formData.signatoryMobileNo,
          signatoryName: formData.signatoryName,
          status: maybe(() => findValueInEnum("RVW", SmopStatusEnum)),
          storeType: maybe(() =>
            findValueInEnum(formData.storeType, StoreTypeEnum)
          ),
          swornDeclarationsFlag: formData.swornDeclarationsFlag,
          tinNo: formData.tinNo,
          vendorCode: formData.vendorCode,
          vertical: getVerticalType(formData)
        }
      }
    });

    return result.data.brandCreate.errors;
  };

  function getVerticalType(
    formData: SellerCreatePageFormData
  ): BrandVerticalEnum {
    if (formData.storeType === StoreTypeEnum.SM_FULFILLED) {
      return BrandVerticalEnum.SM_FULFILLED;
    } else if (formData.storeType === StoreTypeEnum.FOOD_AND_BEVERAGES) {
      return BrandVerticalEnum.FOOD_AND_BEVERAGES;
    } else if (formData.storeType === StoreTypeEnum.GROCERY) {
      return BrandVerticalEnum.GROCERY;
    } else {
      return findValueInEnum(formData.vertical, BrandVerticalEnum);
    }
  }

  const {
    loadMore: loadMoreBrand,
    search: searchBrands,
    result: brandsQueryData
  } = useBrandGroupSearch({
    variables: { ...DEFAULT_INITIAL_SEARCH_DATA, status: "" }
  });
  const sellerBrandChoices = maybe(
    () => brandsQueryData?.data?.search?.edges,
    []
  ).map(edge => edge.node);

  const clusterChoices = maybe(
    () => searchCluster?.result?.data?.search?.edges,
    []
  ).map(edge => edge.node);

  return (
    <>
      <SellerCreatePage
        hasShopifyPermission={hasShopifyPermission}
        onSubmit={handleSubmit}
        onBack={() => navigate(SellerListPath)}
        errors={sellerCreateResult?.data?.brandCreate.errors || []}
        saveButtonBarState={sellerCreateResult.status}
        disabled={sellerCreateResult.loading}
        sellerList={sellerBrandChoices}
        fetchBrands={query => searchBrands(query)}
        fetchMoreBrands={{
          hasMore: maybe(
            () => brandsQueryData.data.search.pageInfo.hasNextPage
          ),
          loading: brandsQueryData.loading,
          onFetchMore: loadMoreBrand
        }}
        clusterList={clusterChoices}
        fetchClusters={query => searchCluster.search(query)}
        fetchMoreClusters={{
          hasMore: maybe(
            () => searchCluster.result.data.search.pageInfo.hasNextPage
          ),
          loading: searchCluster.result.loading,
          onFetchMore: searchCluster.loadMore
        }}
      />
    </>
  );
};

export default SellerCreate;
